<template>
  <BlockFieldset class="project-bank-capital-assets">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <div class="bank-section-grid bank-section-grid--paired">
        <div class="label">{{ $t('bankDeclaredAssets') }}</div>
        <div>
          <InputNumber
            id="bankDeclaredAssets"
            :value="project.bank.declaredAssets"
            @input="setBankField('declaredAssets', $event)"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            suffix=" €"
            locale="de-DE"
            :placeholder="$t('bankDeclaredAssets')"
          />
        </div>
        <template v-for="row in ATTACHMENT_ROWS">
          <div class="label" :key="`${row.attachmentType}-label`">{{ $t(row.labelKey) }}</div>
          <div :key="row.attachmentType">
            <BankAttachmentUpload
              :project="project"
              :attachmentType="row.attachmentType"
              @changed="reloadBlock"
            />
          </div>
        </template>
      </div>
      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('bankSectionCapitalAssets') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import InputNumber from 'primevue/inputnumber';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import BankAttachmentUpload from '@/components/Projects/BankAttachmentUpload';
import { BANK_ATTACHMENT_TYPES } from '@/helpers/bankAttachmentTypes';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { ProjectBankBlockMixin } from '@/mixins/projectBankBlock';
import { PROJECT_BANK_CAPITAL_ASSETS_FIELDS } from './projectBankCapitalAssetsFragment';
import { toProjectBankCapitalAssetsInput } from './projectBankCapitalAssetsInput';

/** Documents of the section in the order of the previous tab. */
const ATTACHMENT_ROWS = [
  {
    labelKey: 'bankAttachmentLiabilitiesOverview',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_LIABILITIES_OVERVIEW,
  },
  {
    labelKey: 'bankAttachmentGiroAccountStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_GIRO_ACCOUNT_STATEMENT,
  },
  {
    labelKey: 'bankAttachmentOverdraftStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_OVERDRAFT_STATEMENT,
  },
  {
    labelKey: 'bankAttachmentSavingsAccountStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_SAVINGS_ACCOUNT_STATEMENT,
  },
  {
    labelKey: 'bankAttachmentConsumerLoanStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_CONSUMER_LOAN_STATEMENT,
  },
  {
    labelKey: 'bankAttachmentSecuritiesDepotStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_SECURITIES_DEPOT_STATEMENT,
  },
  {
    labelKey: 'bankAttachmentRealEstateLoanStatement',
    attachmentType: BANK_ATTACHMENT_TYPES.BANK_REAL_ESTATE_LOAN_STATEMENT,
  },
];

/**
 * Block project-bank-capital-assets ("Kapitalerträge", tab Bank, see feature page projekt-bank): the declared assets, saved with the own button, and the documents of the section.
 * `project` is the block data (`Project.blocks.bankCapitalAssets`); an upload or a delete reloads it. On
 * the unsaved project it shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectBankCapitalAssets',
  components: { BankAttachmentUpload, BlockFieldset, InputNumber },
  mixins: [AccessBlockMixin, BlockSaveMixin, ProjectBankBlockMixin],
  bankBlockField: 'bankCapitalAssets',
  bankBlockFragment: PROJECT_BANK_CAPITAL_ASSETS_FIELDS,
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return { ATTACHMENT_ROWS };
  },
  computed: {
    blockInput() {
      return toProjectBankCapitalAssetsInput(this.project);
    },
  },
  methods: {
    ...mapActions(['updateProjectBankCapitalAssets']),
    saveBlockInput(input) {
      return this.updateProjectBankCapitalAssets({ projectNumber: this.project.number, input });
    },
    setBankField(field, value) {
      this.$set(this.project.bank, field, value);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../styles/bankSectionGrid';
</style>
